import React, { useState, useRef, useEffect } from 'react';
import { IconButton, CircularProgress, Tooltip } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { audioManager } from './AudioPlayer';

const PlayButton = ({ 
  text, 
  userId = 'default', 
  existingAudioUrl = null,
  size = 'small',
  variant = 'outlined' // 'outlined', 'contained', 'text'
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(existingAudioUrl);
  const [hasError, setHasError] = useState(false);
  const audioRef = useRef(null);

  // Initialize audio element
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.preload = 'metadata';
    }

    const audio = audioRef.current;

    const handleEnded = () => {
      setIsPlaying(false);
    };

    const handleError = () => {
      setHasError(true);
      setIsPlaying(false);
      setIsLoading(false);
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      if (audio) {
        audio.pause();
        audio.src = '';
      }
    };
  }, []);

  // Register with audio manager when audio URL is available
  useEffect(() => {
    if (audioUrl && audioRef.current) {
      audioRef.current.src = audioUrl.startsWith('/') 
        ? `${process.env.REACT_APP_API_URL}${audioUrl}` 
        : audioUrl;
      audioManager.register(audioRef.current, setIsPlaying);
    }
  }, [audioUrl]);

  const generateAudio = async () => {
    if (!text || text.trim() === '') {
      console.error('No text provided for TTS generation');
      return null;
    }

    setIsLoading(true);
    setHasError(false);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tts/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_API_TOKEN}`,
        },
        body: JSON.stringify({ 
          text: text.trim(),
          voice: 'en-US-AriaNeural' // Default voice, can be made configurable
        }),
      });

      if (!response.ok) {
        throw new Error(`TTS generation failed: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && data.audio_url) {
        setAudioUrl(data.audio_url);
        return data.audio_url;
      } else {
        throw new Error('Invalid response from TTS service');
      }
    } catch (error) {
      console.error('Error generating TTS audio:', error);
      setHasError(true);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlayPause = async () => {
    if (isLoading) return;

    // If no audio URL exists, generate it first
    if (!audioUrl) {
      const newAudioUrl = await generateAudio();
      if (!newAudioUrl) {
        return; // Generation failed
      }
    }

    const audio = audioRef.current;
    if (!audio) return;

    try {
      if (isPlaying) {
        // Pause current audio
        audio.pause();
        setIsPlaying(false);
      } else {
        // Stop all other audio instances and play this one
        audioManager.play(audio, setIsPlaying);
        setIsPlaying(true);
        await audio.play();
      }
    } catch (error) {
      console.error('Error playing audio:', error);
      setHasError(true);
      setIsPlaying(false);
    }
  };

  const getButtonIcon = () => {
    if (isLoading) {
      return (
        <CircularProgress 
          size={size === 'small' ? 16 : 20} 
          sx={{ color: 'inherit' }} 
        />
      );
    }
    
    if (isPlaying) {
      return <PauseIcon fontSize={size} />;
    }
    
    return <PlayArrowIcon fontSize={size} />;
  };

  const getButtonStyles = () => {
    const baseStyles = {
      minWidth: 'auto',
      width: size === 'small' ? 32 : 40,
      height: size === 'small' ? 32 : 40,
      borderRadius: '50%',
      transition: 'all 0.2s ease-in-out',
      '&:disabled': {
        opacity: 0.5,
      }
    };

    switch (variant) {
      case 'contained':
        return {
          ...baseStyles,
          backgroundColor: '#2196f3',
          color: 'white',
          '&:hover': {
            backgroundColor: '#1976d2',
            transform: 'scale(1.05)',
          },
          '&:disabled': {
            backgroundColor: '#ccc',
          }
        };
      case 'text':
        return {
          ...baseStyles,
          color: '#2196f3',
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: 'rgba(33, 150, 243, 0.1)',
            transform: 'scale(1.05)',
          }
        };
      default: // outlined
        return {
          ...baseStyles,
          color: '#2196f3',
          backgroundColor: 'transparent',
          border: '1px solid #2196f3',
          '&:hover': {
            backgroundColor: '#2196f3',
            color: 'white',
            transform: 'scale(1.05)',
          },
          '&:disabled': {
            borderColor: '#ccc',
            color: '#ccc',
          }
        };
    }
  };

  const getTooltipText = () => {
    if (hasError) return 'Audio generation failed';
    if (isLoading) return 'Generating audio...';
    if (isPlaying) return 'Pause audio';
    return 'Play audio';
  };

  return (
    <Tooltip title={getTooltipText()} arrow>
      <span>
        <IconButton
          onClick={handlePlayPause}
          disabled={isLoading || hasError || !text}
          size={size}
          sx={getButtonStyles()}
          aria-label={getTooltipText()}
        >
          {getButtonIcon()}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default PlayButton;